/**
 * Places Autocomplete helpers for hierarchical location search.
 *
 * Request has NO type restriction so street addresses work (VN house numbers,
 * "1N đường Núi Thành", …). Keep named places as well as administrative areas;
 * activity eligibility is validated when a location is selected.
 */

const ADDRESS_TYPES = new Set([
  'street_address',
  'route',
  'premise',
  'subpremise',
  'geocode',
]);

const SPECIFIC_ADDRESS_TYPES = new Set([
  'street_address',
  'route',
  'premise',
  'subpremise',
]);

/**
 * Normalize for accent-insensitive match (Da Nang ↔ Đà Nẵng).
 */
export function normalizePlaceQuery(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Unrestricted autocomplete (places + cities + regions + street addresses).
 * Type restrictions can hide named venues and VN administrative addresses.
 */
export function buildGeoAutocompleteRequest(
  input: string,
  language: string,
): google.maps.places.AutocompletionRequest {
  return {
    input,
    language,
  };
}

function hasType(
  types: string[],
  allowed: Set<string>,
): boolean {
  return types.some((type) => allowed.has(type));
}

/**
 * Streets and named POIs can display the provider's description immediately.
 * Administrative and generic geocode predictions still need hierarchy details.
 * This controls presentation only, never selection eligibility.
 */
export function isSpecificAddressPrediction(
  prediction: google.maps.places.AutocompletePrediction,
): boolean {
  const types = prediction.types || [];
  if (hasType(types, SPECIFIC_ADDRESS_TYPES)) {
    return true;
  }
  const isAdministrative = types.some(
    (type) =>
      ['political', 'locality', 'postal_town', 'country'].includes(type) ||
      type.startsWith('administrative_area_level_') ||
      type.startsWith('sublocality'),
  );
  return !isAdministrative && (types.includes('establishment') || types.includes('point_of_interest'));
}

function predictionPrimaryName(
  prediction: google.maps.places.AutocompletePrediction,
): string {
  return (
    prediction.structured_formatting?.main_text ||
    prediction.description.split(',')[0] ||
    prediction.description
  );
}

function matchScore(name: string, query: string): number {
  if (!query) {
    return 0;
  }
  if (name === query) {
    return 100;
  }
  if (name.startsWith(query)) {
    return 80;
  }
  const words = name.split(/\s+/);
  if (words.some((word) => word.startsWith(query))) {
    return 60;
  }
  if (name.includes(query)) {
    return 40;
  }
  return 0;
}

function adminRank(
  prediction: google.maps.places.AutocompletePrediction,
  preferAddress: boolean,
): number {
  const types = prediction.types || [];
  if (preferAddress) {
    if (hasType(types, ADDRESS_TYPES)) {
      return 50;
    }
  }
  if (types.includes('country')) {
    return 45;
  }
  if (
    types.includes('locality') ||
    types.includes('postal_town') ||
    types.includes('administrative_area_level_1')
  ) {
    return 40;
  }
  if (types.includes('administrative_area_level_2')) {
    return 20;
  }
  if (
    types.includes('sublocality') ||
    types.includes('sublocality_level_1')
  ) {
    return 10;
  }
  if (hasType(types, ADDRESS_TYPES)) {
    return 25;
  }
  return 15;
}

function looksLikeStreetQuery(input: string): boolean {
  return /\d/.test(input) || /đường|duong|street|rue|avenue|phố|pho/i.test(input);
}

/**
 * Rank provider suggestions without excluding places by category.
 * Street-like queries prefer addresses; city queries prefer admin matches.
 */
export function filterAndSortGeoPredictions(
  predictions: google.maps.places.AutocompletePrediction[],
  input: string,
): google.maps.places.AutocompletePrediction[] {
  const query = normalizePlaceQuery(input);
  const preferAddress = looksLikeStreetQuery(input);
  return predictions
    .slice()
    .sort((left, right) => {
      const leftMatch = matchScore(
        normalizePlaceQuery(predictionPrimaryName(left)),
        query,
      );
      const rightMatch = matchScore(
        normalizePlaceQuery(predictionPrimaryName(right)),
        query,
      );
      if (leftMatch !== rightMatch) {
        return rightMatch - leftMatch;
      }
      return (
        adminRank(right, preferAddress) - adminRank(left, preferAddress)
      );
    });
}
